import { motion, AnimatePresence } from 'framer-motion';

interface CenterPanelProps {
  ticketGrid: number[][];
  currentNumber: number | null;
  calledNumbers: number[];
  markedNumbers: number[];
  onMarkCell: (num: number) => void;
  isOut: boolean;
}

export function CenterPanel({
  ticketGrid,
  currentNumber,
  calledNumbers,
  markedNumbers,
  onMarkCell,
  isOut,
}: CenterPanelProps) {

  return (
    <div className="flex flex-col justify-center items-center h-full w-full relative">
      
      {/* 1. Ticket card wrapper (occupies ~70% area) */}
      <div 
        style={{
          boxShadow: '0 12px 40px rgba(0,0,0,0.5), inset 0 0 20px rgba(255,255,255,0.02)',
        }}
        className="glass rounded-3xl p-3 md:p-6 w-full max-w-2xl border border-white/10 relative overflow-hidden"
      >
        
        {/* Ticket Header & Info */}
        <div className="flex items-center justify-between mb-3 select-none">
          <div className="flex flex-col text-left">
            <span className="font-display text-xs md:text-sm font-black text-white tracking-wide">
              YOUR TICKET
            </span>
            <span className="text-[8px] md:text-[9px] uppercase tracking-wider text-surface-400 font-bold">
              Mark matching called numbers
            </span>
          </div>
        </div>

        {/* 3x9 Ticket Grid */}
        <div 
          style={{
            background: 'linear-gradient(135deg, rgba(30, 27, 75, 0.4) 0%, rgba(15, 23, 42, 0.6) 100%)',
            boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.6)',
          }}
          className="rounded-2xl p-2 md:p-4 border border-white/5 shadow-2xl relative"
        >
          {isOut && (
            <div className="absolute inset-0 bg-red-950/70 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center border border-red-500/30 z-30 select-none animate-fade-in">
              <span className="text-3xl animate-bounce">⚠️</span>
              <span className="font-display font-black text-red-400 text-sm md:text-lg uppercase tracking-widest mt-2 drop-shadow-md">
                YOU ARE OUT!
              </span>
              <span className="text-[8px] md:text-[10px] text-red-300 font-bold uppercase tracking-wider mt-1 text-center px-4">
                You clicked an uncalled number or made an invalid claim
              </span>
            </div>
          )}
          <div className="grid grid-rows-3 gap-1 md:gap-2">
            {ticketGrid.map((row, rIdx) => (
              <div key={rIdx} className="grid grid-cols-9 gap-1 md:gap-2">
                {row.map((num, cIdx) => {
                  const isBlank = num === 0;
                  const isMarked = markedNumbers.includes(num);

                  // Define styling priorities (only check blank and marked, do not hint called/current numbers)
                  const cellStyles = isBlank
                    ? 'border-white/2 bg-slate-950/20 cursor-default'
                    : isMarked
                      ? 'bg-gradient-to-tr from-purple-700 to-pink-600 border-purple-400/40 text-white cursor-pointer shadow-glow-accent'
                      : 'bg-white/5 border-white/5 text-white/50 cursor-pointer hover:bg-white/10';

                  return (
                    <motion.div
                      key={cIdx}
                      whileHover={isBlank ? {} : { scale: 1.05 }}
                      whileTap={isBlank ? {} : { scale: 0.95 }}
                      onClick={isBlank ? undefined : () => onMarkCell(num)}
                      style={{
                        boxShadow: isMarked
                          ? '0 0 10px rgba(236,72,153,0.3)'
                          : 'none',
                      }}
                      className={`aspect-square flex items-center justify-center rounded-lg md:rounded-xl border font-display font-black text-sm sm:text-base md:text-lg transition-all duration-300 select-none ${cellStyles}`}
                    >
                      {!isBlank && (
                        <span>{num.toString().padStart(2, '0')}</span>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
